import React, { useCallback, useState, useMemo } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import useUpdateAction from 'core/hooks/useUpdateAction'
import ModalForm from 'core/elements/modal/ModalForm'
import useParams from 'core/hooks/useParams'
import { enableHypervisorScheduling, disableHypervisorScheduling } from '../actions'
import { activeRoleHostsByCluster } from '../clusters/selectors'
import Info from 'core/components/validatedForm/Info'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { useSelector } from 'react-redux'
import { CLUSTER_HOST_SCHEDULING } from 'app/constants'
import { keys } from 'ramda'

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    gap: 16,
    display: 'grid',
  },
  errorIcon: {
    marginRight: 4,
  },
  tabbed: {
    marginTop: 8,
    marginLeft: 16,
    display: 'grid',
    gap: 4,
  },
}))

interface Props {
  rows: any[]
  onClose: (boolean) => void
}

export default function ToggleMaintenanceModeDialog({ rows: hosts, onClose }: Props) {
  const classes = useStyles()

  const roleHostsByCluster = useSelector(activeRoleHostsByCluster)
  const isLastHypervisorByCluster = useMemo(() => {
    const hostsToDisable =
      hosts
        ?.filter((host) => host?.schedulingStatus === CLUSTER_HOST_SCHEDULING.enabled)
        ?.map((host) => host?.id) || []
    const clusterNames = keys(roleHostsByCluster)
    return clusterNames.reduce((accum, clusterName) => {
      const activeClusterHypervisors = roleHostsByCluster[clusterName]?.hypervisor
      const disablingAllHypervisors =
        !!activeClusterHypervisors.length &&
        activeClusterHypervisors.every((hypervisor) => hostsToDisable.includes(hypervisor?.id))
      return {
        ...accum,
        [clusterName]: disablingAllHypervisors,
      }
    }, {})
  }, [roleHostsByCluster, hosts])

  const warningClusters = useMemo(() => {
    const clusters = keys(isLastHypervisorByCluster)
    return clusters.filter((cluster) => {
      return !!isLastHypervisorByCluster[cluster]
    })
  }, [isLastHypervisorByCluster])

  const {
    update: enableHypervisor,
    updating: enablingHypervisor,
    error: errorEnablingHypervisor,
    reset: resetEnableHypervisor,
  } = useUpdateAction(enableHypervisorScheduling)
  const {
    update: disableHypervisor,
    updating: disablingHypervisor,
    error: errorDisablingHypervisor,
    reset: resetDisableHypervisor,
  } = useUpdateAction(disableHypervisorScheduling)

  const handleSubmit = useCallback(async () => {
    const responses = await Promise.all(
      hosts.map((host) => {
        const body = {
          host: host.id,
          binary: 'nova-compute',
        }
        return host.hypervisor.status === 'disabled'
          ? enableHypervisor({ id: host.hypervisor.id, body })
          : disableHypervisor({ id: host.hypervisor.id, body })
      }),
    )

    // Check success response
    if (responses.every((response) => response.success)) {
      handleClose(true)
    }
  }, [hosts])

  const handleClose = (success?: boolean) => {
    resetEnableHypervisor()
    resetDisableHypervisor()
    onClose(success)
  }

  return (
    <ModalForm
      title={'Enable/Disable Scheduling'}
      onSubmit={handleSubmit}
      onClose={() => handleClose()}
      loading={false}
      submitting={enablingHypervisor || disablingHypervisor}
      error={errorEnablingHypervisor || errorDisablingHypervisor}
      submitTitle={`Submit`}
      panel="dialog"
      maxWidth={800}
      open
    >
      <div className={classes.fields}>
        {!!warningClusters?.length && (
          <Info error>
            <Text variant="body2">
              <FontAwesomeIcon className={classes.errorIcon}>triangle-exclamation</FontAwesomeIcon>
              <b>WARNING:</b> You are about to disable scheduling on the last remaining host with
              hypervisor role in {warningClusters.length === 1 ? 'cluster' : 'clusters'}{' '}
              <b>{warningClusters.join(', ')}</b>. Disabling scheduling on these hosts will have
              following side effects:
            </Text>
            <div className={classes.tabbed}>
              <Text variant="body2">
                <b>1.</b> VM creation will fail on the cluster(s).
              </Text>
              <Text variant="body2">
                <b>2.</b> All VM operations on the cluster(s) other than editing VM metadata and
                migrating the VM off the cluster will fail.
              </Text>
            </div>
          </Info>
        )}
        <Text variant="body2">You are about to Enable/Disable scheduling on the below hosts: </Text>
        {hosts.map((host) => (
          <Text>
            <b>{host.name}</b> -{' '}
            {host.hypervisor.status === 'disabled' ? 'Enable Scheduling' : 'Disable Scheduling'}
          </Text>
        ))}
        <Text variant="body2">
          Disabling scheduling on a host will prevent additional VMs from being scheduled on it.
        </Text>
        <Text variant="body2">
          Enabling scheduling on a host will allow new VMs to be scheduled on it.
        </Text>
      </div>
    </ModalForm>
  )
}
